import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  TextField,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Link,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getLoanDetail,
  getChargeTypes,
  addCharge,
  applyPayment,
  applyBackdatedPayment,
  type BackdatePaymentResponse,
  startLoanReplay,
  getLoanReplayReport,
  type LoanDetailResponse,
  type ChargeTypeItem,
  type LoanReplayReport,
  type ReplayStep,
} from '../../endpoints/loans';
import { formatCalendarDate } from '../../utils/formatCalendarDate';

const LoansTestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [paymentAmount, setPaymentAmount] = useState('');
  const [postingDate, setPostingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [backdateAmount, setBackdateAmount] = useState('');
  const [backdatePostingDate, setBackdatePostingDate] = useState('');
  const [backdateMethod, setBackdateMethod] = useState('check');
  const [backdatePending, setBackdatePending] = useState(false);
  const [backdateResult, setBackdateResult] =
    useState<BackdatePaymentResponse | null>(null);
  const [selectedChargeTypeId, setSelectedChargeTypeId] = useState<string>('');
  const [chargeStartDate, setChargeStartDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [replayAsOfDate, setReplayAsOfDate] = useState('');
  const [replayTolerance, setReplayTolerance] = useState('1');
  const [replayTaskLogId, setReplayTaskLogId] = useState<string | null>(null);
  const [replayStatus, setReplayStatus] = useState<string | null>(null);
  const [replayReport, setReplayReport] = useState<LoanReplayReport | null>(
    null
  );
  const [replayError, setReplayError] = useState<string | null>(null);
  const [replayPending, setReplayPending] = useState(false);
  const [showReplaySteps, setShowReplaySteps] = useState(false);

  const {
    data: loan,
    isLoading,
    error,
    refetch,
  } = useApiGet<LoanDetailResponse>(['loan', id!], () => getLoanDetail(id!), {
    enabled: !!id,
  });

  const { data: chargeTypes = [] } = useApiGet<ChargeTypeItem[]>(
    ['loan-charge-types'],
    getChargeTypes,
    { enabled: true }
  );

  const { mutate: doApplyPayment, isPending: paymentPending } = useApiSend(
    (body: {
      amount: number;
      posting_date: string;
      payment_method: string;
      user_id?: string;
    }) => applyPayment(body),
    () => {
      setPaymentAmount('');
      setActionError(null);
      refetch();
    },
    (err: Error) =>
      setActionError((err as any)?.response?.data?.error ?? err.message),
    [['loan', id]]
  );

  useEffect(() => {
    if (!replayTaskLogId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    const stopPolling = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    const poll = async () => {
      try {
        const res = await getLoanReplayReport(replayTaskLogId);
        if (cancelled) return;
        setReplayStatus(res.status);
        if (res.report) setReplayReport(res.report);
        if (
          res.status === 'completed' ||
          res.status === 'error-go' ||
          res.status === 'suspended'
        ) {
          setReplayPending(false);
          if (res.status === 'error-go' && !res.report) {
            setReplayError(res.message ?? 'Replay failed');
          }
          stopPolling();
        }
      } catch (err) {
        if (!cancelled) {
          setReplayPending(false);
          setReplayError((err as Error).message);
          stopPolling();
        }
      }
    };
    poll();
    timer = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [replayTaskLogId]);

  const handleStartReplay = async () => {
    if (!id) return;
    setReplayTaskLogId(null);
    setReplayPending(true);
    setReplayError(null);
    setReplayReport(null);
    setReplayStatus('processing');
    setShowReplaySteps(false);
    try {
      const res = await startLoanReplay({
        source_loan_id: id,
        as_of_date: replayAsOfDate.trim() || undefined,
        tolerance_cents: replayTolerance.trim()
          ? Number(replayTolerance)
          : undefined,
      });
      setReplayTaskLogId(res.task_log_id);
    } catch (err) {
      setReplayPending(false);
      setReplayError(
        (err as any)?.response?.data?.error ?? (err as Error).message
      );
    }
  };

  const { mutate: doAddCharge, isPending: chargePending } = useApiSend(
    (body: { name: string; amount: number; start_date: string }) =>
      addCharge(id!, body),
    () => {
      setSelectedChargeTypeId('');
      setActionError(null);
      refetch();
    },
    (err: Error) =>
      setActionError((err as any)?.response?.data?.error ?? err.message),
    [['loan', id]]
  );

  if (!id) return <Typography>Missing loan ID</Typography>;
  if (isLoading) return <CircularProgress />;
  if (error || !loan) {
    return (
      <Typography color="error">
        {(error as any)?.response?.data?.error ??
          (error as Error)?.message ??
          'Loan not found'}
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button
          size="small"
          onClick={() => navigate('/administration/loans-test')}
        >
          ← Back to list
        </Button>
        <Typography variant="h5" sx={{ mt: 1 }}>
          Loan {loan.id.slice(0, 8)}…
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Summary" />
          <CardContent>
            <Typography>User ID: {loan.user_id}</Typography>
            <Typography>
              Amount: {loan.amount.toFixed(2)} {loan.currency}
            </Typography>
            <Typography>Status: {loan.status}</Typography>
            <Typography>First payment: {loan.first_payment_date}</Typography>
            <Typography>
              Next payment: {loan.next_payment_date ?? '—'}
            </Typography>
            {loan.start_date != null && (
              <Typography>Start date: {loan.start_date}</Typography>
            )}
            {loan.anniversary_date != null && (
              <Typography>Anniversary date: {loan.anniversary_date}</Typography>
            )}
            <Typography>
              Interest rate: {loan.interest_rate.toFixed(2)}%
            </Typography>
            <Typography>
              Principal only: {loan.principal_only ? 'Yes' : 'No'}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Current balances</Typography>
              <Typography>
                Principal: {loan.current_principal_balance.toFixed(2)}
              </Typography>
              <Typography>
                Interest: {loan.current_interest_balance.toFixed(2)}
              </Typography>
              <Typography>
                Deferred: {loan.current_deferred_balance.toFixed(2)}
              </Typography>
              <Typography>
                Charges: {loan.current_charges_balance.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Add payment" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  label="Amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="date"
                  label="Posting date"
                  value={postingDate}
                  onChange={(e) => setPostingDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  label="Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    !paymentAmount ||
                    Number(paymentAmount) <= 0 ||
                    paymentPending
                  }
                  onClick={() =>
                    doApplyPayment({
                      amount: Number(paymentAmount),
                      posting_date: postingDate,
                      payment_method: paymentMethod,
                      user_id: loan.user_id,
                    })
                  }
                >
                  {paymentPending ? 'Applying…' : 'Apply payment'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardHeader title="Backdated payment" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Post a payment with an effective date in the past. Entry date is
              today; later payments may be deleted and replayed.
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  label="Amount"
                  value={backdateAmount}
                  onChange={(e) => setBackdateAmount(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="date"
                  label="Posting date (past)"
                  value={backdatePostingDate}
                  onChange={(e) => setBackdatePostingDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  label="Method"
                  value={backdateMethod}
                  onChange={(e) => setBackdateMethod(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={
                    !backdateAmount ||
                    Number(backdateAmount) <= 0 ||
                    !backdatePostingDate ||
                    backdatePending
                  }
                  onClick={async () => {
                    setBackdatePending(true);
                    setActionError(null);
                    setBackdateResult(null);
                    try {
                      const res = await applyBackdatedPayment({
                        user_id: loan.user_id,
                        amount: Number(backdateAmount),
                        posting_date: backdatePostingDate,
                        payment_method: backdateMethod,
                      });
                      setBackdateResult(res);
                      setBackdateAmount('');
                      refetch();
                    } catch (err) {
                      setActionError(
                        (err as any)?.response?.data?.error ??
                          (err as Error).message
                      );
                    } finally {
                      setBackdatePending(false);
                    }
                  }}
                >
                  {backdatePending ? 'Applying…' : 'Apply backdated payment'}
                </Button>
              </Grid>
            </Grid>
            {backdateResult && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Payment {backdateResult.payment_id.slice(0, 8)}… posted on{' '}
                {backdateResult.posting_date}.
                {backdateResult.deleted_payment_ids &&
                  backdateResult.deleted_payment_ids.length > 0 && (
                    <>
                      {' '}
                      Deleted {backdateResult.deleted_payment_ids.length} later
                      payment(s).
                    </>
                  )}
                {backdateResult.replayed_payment_ids &&
                  backdateResult.replayed_payment_ids.length > 0 && (
                    <>
                      {' '}
                      Replayed {backdateResult.replayed_payment_ids.length}{' '}
                      payment(s).
                    </>
                  )}
                {backdateResult.reapplied_charge_ids &&
                  backdateResult.reapplied_charge_ids.length > 0 && (
                    <>
                      {' '}
                      Reapplied {
                        backdateResult.reapplied_charge_ids.length
                      }{' '}
                      charge(s).
                    </>
                  )}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardHeader title="Add charge" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="charge-type-label">Charge type</InputLabel>
                  <Select
                    labelId="charge-type-label"
                    label="Charge type"
                    value={selectedChargeTypeId}
                    onChange={(e) =>
                      setSelectedChargeTypeId(e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>Select a charge</em>
                    </MenuItem>
                    {chargeTypes.map((ct) => (
                      <MenuItem key={ct.id} value={ct.id}>
                        {ct.name} — {ct.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  type="date"
                  label="Start date"
                  value={chargeStartDate}
                  onChange={(e) => setChargeStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    !selectedChargeTypeId || !chargeStartDate || chargePending
                  }
                  onClick={() => {
                    const selected = chargeTypes.find(
                      (ct) => ct.id === selectedChargeTypeId
                    );
                    if (!selected) return;
                    doAddCharge({
                      name: selected.name,
                      amount: selected.amount,
                      start_date: chargeStartDate,
                    });
                  }}
                >
                  {chargePending ? 'Adding…' : 'Add charge'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardHeader title="Replay validation" />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Clone this loan onto a replay user and re-apply charges, payments,
              and anniversary billing through the engine. Single-loan users
              only.
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="date"
                  label="As-of date (optional)"
                  value={replayAsOfDate}
                  onChange={(e) => setReplayAsOfDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  label="Tolerance (cents)"
                  value={replayTolerance}
                  onChange={(e) => setReplayTolerance(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={replayPending}
                  onClick={handleStartReplay}
                >
                  {replayPending ? 'Running replay…' : 'Replay & compare'}
                </Button>
              </Grid>
            </Grid>
            {replayPending && (
              <Box
                sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2">
                  Task {replayTaskLogId?.slice(0, 8)}… (
                  {replayStatus ?? 'queued'})
                </Typography>
              </Box>
            )}
            {replayError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {replayError}
              </Alert>
            )}
            {replayReport && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={replayReport.passed ? 'success' : 'warning'}>
                  {replayReport.passed
                    ? 'Replay passed — balances match within tolerance.'
                    : 'Replay found mismatches (see report below).'}
                </Alert>
                {replayReport.replica_loan_id && (
                  <Typography sx={{ mt: 1 }}>
                    Replica loan:{' '}
                    <Link
                      href={`/administration/loans-test/${replayReport.replica_loan_id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/administration/loans-test/${replayReport.replica_loan_id}`
                        );
                      }}
                    >
                      {replayReport.replica_loan_id.slice(0, 12)}…
                    </Link>
                  </Typography>
                )}
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Final balances
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell align="right">Source</TableCell>
                        <TableCell align="right">Replica</TableCell>
                        <TableCell align="right">Diff</TableCell>
                        <TableCell>Match</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(
                        [
                          ['Principal', replayReport.final.principal],
                          ['Interest', replayReport.final.interest],
                          ['Deferred', replayReport.final.deferred],
                          ['Charges', replayReport.final.charges],
                        ] as const
                      ).map(([label, field]) => (
                        <TableRow key={label}>
                          <TableCell>{label}</TableCell>
                          <TableCell align="right">
                            {field.source.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {field.replica.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {field.diff.toFixed(2)}
                          </TableCell>
                          <TableCell>{field.match ? '✓' : '✗'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => setShowReplaySteps((v) => !v)}
                >
                  {showReplaySteps ? 'Hide steps' : 'Show steps'}
                </Button>
                <Collapse in={showReplaySteps}>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Detail</TableCell>
                          <TableCell>Match</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {replayReport.steps.map((step: ReplayStep) => {
                          const highlight =
                            replayReport.first_mismatch?.index === step.index;
                          return (
                            <TableRow
                              key={step.index}
                              sx={
                                highlight
                                  ? { backgroundColor: 'warning.light' }
                                  : undefined
                              }
                            >
                              <TableCell>{step.index}</TableCell>
                              <TableCell>{step.event_date}</TableCell>
                              <TableCell>{step.event_type}</TableCell>
                              <TableCell>{step.detail}</TableCell>
                              <TableCell>
                                {step.step_match ? '✓' : '✗'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {actionError && (
        <Grid item xs={12}>
          <Typography color="error">{actionError}</Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Balance log history (audit)
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Log date</TableCell>
                <TableCell align="right">Principal</TableCell>
                <TableCell align="right">Interest</TableCell>
                <TableCell align="right">Charges</TableCell>
                <TableCell align="right">New principal</TableCell>
                <TableCell align="right">New interest</TableCell>
                <TableCell align="right">New charges</TableCell>
                <TableCell>Ref</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loan.balance_logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>No log entries yet.</TableCell>
                </TableRow>
              ) : (
                loan.balance_logs.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{formatCalendarDate(log.log_date)}</TableCell>
                    <TableCell align="right">
                      {log.principal_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.interest_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.charges_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_principal_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_interest_balance.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_charges_balance.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {log.payment_allocation_id &&
                        `pay:${log.payment_allocation_id.slice(0, 6)}…`}
                      {log.charge_id && ` chg:${log.charge_id.slice(0, 6)}…`}
                      {log.deferred_interest_log_id &&
                        ` dil:${log.deferred_interest_log_id.slice(0, 6)}…`}
                      {!log.payment_allocation_id &&
                        !log.charge_id &&
                        !log.deferred_interest_log_id &&
                        '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default LoansTestDetail;
